import classNames from 'classnames'
import { useCallback, useState } from 'react'
import Select from 'react-select'

import type { ReactElement, ReactNode } from 'react'
import type { MenuProps, OptionProps, Props as SelectProps, StylesConfig } from 'react-select'

import { Menu, Control, Option, ValueContainer } from './components'

import InputWrapper from '../InputWrapper'

import './styles.scss'

const styles: StylesConfig = {
  container: (styles) => ({
    ...styles,
    outline: 'none',
    width: '100%',
  }),
  control: (styles) => ({
    ...styles,
    '&:focus-within': {
      border: 0,
      boxShadow: 'none',
      overflow: 'hidden',
    },
    '&:hover': {
      border: 0,
      boxShadow: 'none',
    },
    backgroundColor: '#f9f9f9',
    border: 0,
    borderRadius: 5,
    cursor: 'pointer',
    maxHeight: 200,
    minHeight: 0,
    position: 'relative',
    width: '100%',
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    display: 'none',
  }),
  indicatorsContainer: (styles) => ({
    ...styles,
    height: '100%',
    position: 'sticky',
  }),
  input: (styles) => ({
    ...styles,
    color: '#1b1d1f',
    fontSize: '1.4rem',
    fontWeight: 500,
    width: '100%',
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: '#fff',
    border: '0.1rem solid #626262',
    borderRadius: 5,
    maxWidth: 300,
    minWidth: '100%',
    zIndex: 99,
  }),
  menuList: (styles) => ({
    ...styles,
    '&:first-of-type': {
      borderTopLeftRadius: '0.5rem',
      borderTopRightRadius: '0.5rem',
    },
    '&:last-child': {
      borderBottomLeftRadius: '0.5rem',
      borderBottomRightRadius: '0.5rem',
    },
    padding: 0,
  }),
  menuPortal: (styles) => ({
    ...styles,
    zIndex: 9999,
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    alignItems: 'center',
    backgroundColor: isSelected ? '#ffcdb6' : isFocused ? '#ffcdb6' : 'inherit',
    color: '#626262',
    columnGap: '0.5rem',
    cursor: 'pointer',
    display: 'flex',
    fontSize: '1.1rem',
    fontWeight: 600,
    padding: '0.8rem 1.6rem',
    width: '100%',
  }),
  placeholder: (styles) => ({
    ...styles,
    color: '#aeaeae',
    fontSize: '1.4rem',
    fontWeight: 500,
    zIndex: 1,
  }),
  singleValue: (styles) => ({
    ...styles,
    marginLeft: '0.4rem',
  }),
  valueContainer: (styles) => ({
    ...styles,
    height: '100%',
    padding: '0 0 0 1.2rem',
  }),
}

export interface Props extends Omit<SelectProps, 'isDisabled' | 'isMulti'> {
  checkboxInitialValue?: boolean
  disabled?: boolean
  error?: ReactNode
  isCheckboxDisabled?: boolean
  isReadOnly?: boolean
  label?: string
  labelPosition?: 'top' | 'left'
  menuClassName?: string
  onCheckboxChanged?: (checkboxValue: boolean) => void
  size?: Exclude<SIZES, 'large'>
  tooltip?: ReactNode
  withExcept?: boolean
  wrapperClassName?: string
}

const SelectInput = ({
  checkboxInitialValue,
  className,
  disabled,
  error,
  isCheckboxDisabled,
  isClearable = true,
  isReadOnly = false,
  label,
  labelPosition = 'top',
  menuClassName,
  menuIsOpen,
  onCheckboxChanged,
  optionType,
  placeholder = 'Select',
  required,
  size = 'medium',
  tooltip,
  value,
  withExcept,
  wrapperClassName,
  ...props
}: Props): ReactElement => {
  /** Local state */

  const [isMenuOpen, setIsMenuOpen] = useState(menuIsOpen)

  const classes = classNames('select-input-wrapper', className, {
    'input-component--erroneous': !!error,
    'select-input-component--read-only': isReadOnly,
  })

  /** Handlers */

  const handleMenuOpenClose = (isOpen: boolean) => () => setIsMenuOpen(isOpen)

  const MenuCallback = useCallback(
    (menuProps: MenuProps) => Menu(menuProps, classNames('multi-select-input-menu', menuClassName)),
    [menuClassName]
  )

  const OptionsCallback = useCallback(
    (optionProps: OptionProps) => Option(optionProps, props.components?.Option),
    [props.components?.Option]
  )

  /** Render */

  return (
    <InputWrapper
      checkboxInitialValue={checkboxInitialValue}
      className={classes}
      disabled={disabled}
      isCheckboxDisabled={isCheckboxDisabled}
      label={label}
      labelPosition={labelPosition}
      onCheckboxChanged={onCheckboxChanged}
      required={required}
      size={size}
      tooltip={tooltip}
      withCheckbox={withExcept}
      wrapperClassName={wrapperClassName}
    >
      <Select
        {...props}
        className="react-select-container"
        classNamePrefix="react-select"
        components={{
          Control,
          Menu: MenuCallback,
          Option: OptionsCallback,
          ValueContainer,
        }}
        hideSelectedOptions={false}
        isClearable={!isReadOnly && isClearable && !!isMenuOpen}
        isDisabled={disabled}
        isMulti={false}
        isOptionDisabled={() => isReadOnly}
        isSearchable={!isReadOnly}
        loadingMessage={() => 'Loading ...'}
        menuIsOpen={isMenuOpen}
        menuShouldScrollIntoView={false}
        onMenuClose={handleMenuOpenClose(false)}
        onMenuOpen={handleMenuOpenClose(true)}
        optionType={optionType}
        placeholder={placeholder}
        styles={styles}
        value={value}
      />
    </InputWrapper>
  )
}

export default SelectInput
